import { Button } from '@dapp/multi-lib/sdaoui/Button';
import React, { useState } from 'react';
import { Body2, Title4, Title1 } from 'components/Typography';
import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { useTranslation } from 'react-i18next';
import { useConnection } from 'wagmi';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { UserBalanceItem } from 'structure/pv2/vault/components/UserBalanceItem';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { ConnectButton } from 'components/ConnectButton';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { ModalVaultDeposit } from 'structure/pv2/vault/containers/ModalVaultDeposit';
import { ModalVaultWithdraw } from 'structure/pv2/vault/containers/ModalVaultWithdraw';
import { VaultIdentity } from 'structure/pv2/vault/utils/vault';
import { useVault } from 'structure/pv2/vault/hooks/useVault';
import { ethers } from 'ethers';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';
import { useTokenBalance } from 'utils/hooks/useTokenBalance';
import { getDepositTokenList } from 'structure/pv2/vault/utils/depositTokenList';
import { useUserAssetsQuoted } from 'structure/pv2/vault/hooks/useUserAssetsQuoted';
import { useUserTotalAssets } from 'structure/pv2/vault/hooks/useUserTotalAssets';
import { formatUnits } from 'utils/units';
import { useTokenListInfo } from 'structure/capital-manager/hooks/useTokenListInfo';
import { AnimatedNumber } from 'structure/pv2/vault/components/AnimatedNumber';
import { refreshAfterTransaction } from 'structure/pv2/vault/utils/refreshAfterTransaction';
import { canDepositIntoVault } from 'structure/pv2/vault/utils/canDepositIntoVault';
import { refetchChangedBalance } from 'structure/pv2/vault/utils/refetchChangedBalance';

const REFETCH_INTERVAL = 14000;

interface UserBalanceProps {
  vaultIdentity: VaultIdentity;
}

export const UserBalance = (props: UserBalanceProps) => {
  const { isConnected } = useConnection();
  const { vaultIdentity } = props;

  return (
    <Surface rounded='large'>
      {isConnected ? (
        <UserBalanceAuthed vaultIdentity={vaultIdentity} />
      ) : (
        <UserBalanceUnAuthed />
      )}
    </Surface>
  );
};

const UserBalanceUnAuthed = () => {
  const { t } = useTranslation('vault');
  return (
    <div className='flex flex-col gap-5'>
      <Title1 className='text-[#D9D9D9]'>{t('BALANCE')}</Title1>
      <div className='flex flex-col'>
        <Title4>{t('CONNECT_WALLET_LABEL')}</Title4>
        <Body2 className='text-[#A6A6A6]'>
          {t('CONNECT_WALLET_DESCRIPTION')}
        </Body2>
      </div>
      <AuthButton />
    </div>
  );
};

interface UserBalanceAuthProps {
  vaultIdentity: VaultIdentity;
}

const UserBalanceAuthed = (props: UserBalanceAuthProps) => {
  const { t } = useTranslation('vault');
  const { vaultIdentity } = props;

  const account = useConnection();
  const vaultQuery = useVault(vaultIdentity);

  const assetQuery = useUserAssetsQuoted({
    userAddress: account.address as `0x${string}`,
    quoteAddress: vaultQuery.data?.depositToken as `0x${string}`,
    tokenAddresses: [vaultIdentity.address],
    query: {
      refetchInterval: REFETCH_INTERVAL,
      staleTime: REFETCH_INTERVAL,
      enabled: !!account.address && !!vaultQuery.data?.depositToken,
    },
  });

  const balanceInUsdQuery = useUserTotalAssets({
    userAddress: account.address as `0x${string}`,
    tokens: [
      { address: vaultIdentity.address, chainId: vaultIdentity.chainId },
    ],
    query: {
      refetchInterval: REFETCH_INTERVAL,
      staleTime: REFETCH_INTERVAL,
      enabled: !!account.address,
    },
  });

  const tokenListQuery = useTokenListInfo({
    chainId: vaultIdentity.chainId,
    tokenList: [vaultQuery.data?.depositToken as `0x${string}`],
    query: {
      enabled: !!vaultQuery.data?.depositToken,
    },
  });

  const depositTokenDecimals =
    tokenListQuery.data?.map[vaultQuery.data?.depositToken || '']?.decimals ||
    18;

  const depositTokenBalance = formatUnits(
    assetQuery.data?.quotesTotal || 0,
    depositTokenDecimals
  );

  const depositTokenSymbol =
    tokenListQuery.data?.map[vaultQuery.data?.depositToken || '']?.symbol || '';

  const balanceQuery = useTokenBalance({
    address: account.address as `0x${string}`,
    token: vaultIdentity.address,
    chainId: vaultIdentity.chainId,
    query: {
      enabled: true,
      staleTime: REFETCH_INTERVAL,
      refetchInterval: REFETCH_INTERVAL,
    },
  });

  const [depositModal, setDepositModal] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const canDeposit = canDepositIntoVault(vaultQuery.data);

  const handleOpenDepositModal = (open: boolean) => {
    if (open && !canDeposit) {
      return;
    }

    setDepositModal(open);
  };

  const handleOpenWithdrawModal = (open: boolean) => {
    setWithdrawModal(open);
  };

  const handleBalanceChange = async () => {
    const previousShares = balanceQuery.data?.value;

    await refreshAfterTransaction([
      () => assetQuery.refetch(),
      () => balanceInUsdQuery.refetch(),
      () =>
        refetchChangedBalance({
          previousValue: previousShares,
          refetch: balanceQuery.refetch,
        }),
    ]);
  };

  const sharesFormated = ethers.utils.formatUnits(
    balanceQuery.data?.value || 0,
    balanceQuery.data?.decimals || 0
  );

  const usdValue = balanceInUsdQuery.data?.quotesTotal
    ? ethers.utils.formatUnits(balanceInUsdQuery.data.quotesTotal, 6)
    : 0;

  const depositTokenList = getDepositTokenList(vaultIdentity.chainId);

  return (
    <div className='flex flex-col gap-5'>
      <ModalVaultDeposit
        vaultIdentity={vaultIdentity}
        open={depositModal}
        depositTokenAddress={depositTokenList[0].address}
        depositTokenList={depositTokenList}
        onDepositSuccess={handleBalanceChange}
        onOpenChange={handleOpenDepositModal}
      />
      <ModalVaultWithdraw
        vaultIdentity={vaultIdentity}
        open={withdrawModal}
        onWithdrawSuccess={handleBalanceChange}
        onOpenChange={handleOpenWithdrawModal}
      />
      <Title1 className='text-[#D9D9D9]'>{t('BALANCE')}</Title1>
      <div className='flex flex-row flex-wrap gap-3 lg:justify-start'>
        <UserBalanceItem
          className='order-1'
          icon={
            <Icon
              icon='pie_chart_outline'
              color={'#C1CAD6'}
              width={16}
              height={16}
            />
          }
          label={t('MY_SHARES')}
          value={
            <div className='flex flex-row items-center gap-1 font-mono text-sm'>
              {sharesFormated}
              <span className='text-[#BFBFBF]'>
                {balanceQuery.data?.symbol}
              </span>
            </div>
          }
        />
        <UserBalanceItem
          className='order-3'
          icon={
            <Icon
              icon='attach_money'
              color={'#C1CAD6'}
              width={16}
              height={16}
            />
          }
          label={t('APPROX_IN_USD')}
          value={<div className='font-mono text-sm'>{formatUsd(usdValue)}</div>}
        />
        <UserBalanceItem
          className='order-2'
          icon={
            <Icon
              icon='pie_chart_outline'
              color={'#C1CAD6'}
              width={16}
              height={16}
            />
          }
          label={t('BALANCE_IN_DEPOSIT_TOKEN')}
          value={
            <div className='flex flex-row items-center gap-1 font-mono text-sm'>
              <AnimatedNumber
                value={depositTokenBalance}
                animationDuration={REFETCH_INTERVAL}
                decimals={depositTokenDecimals}
              />
              <span className='text-[#BFBFBF]'>{depositTokenSymbol}</span>
            </div>
          }
        />
      </div>
      <div className='flex gap-4 self-end'>
        <Button
          style='outline'
          disabled={!(balanceQuery.data?.value || 0 > 0)}
          onClick={() => setWithdrawModal(true)}
        >
          {t('WITHDRAW')}
        </Button>
        <Button
          disabled={!canDeposit}
          onClick={() => handleOpenDepositModal(true)}
        >
          {t('DEPOSIT')}
        </Button>
      </div>
    </div>
  );
};

const AuthButton = () => {
  const { isConnected } = useConnection();

  const csr = useClientRenderDelay();

  const isMobile = useIsMobile();

  const hideConnectButtonOnMobile = isMobile && !isConnected;
  if (hideConnectButtonOnMobile) {
    return null;
  }

  return (
    <div className='self-end'>
      {csr && !isConnected && <ConnectButton className='max-w-max' />}
    </div>
  );
};
